import { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { signOut } from '../lib/auth';
import { useThemePreference } from '../lib/theme';
import { BrandMark } from './BrandMark';
import { IconName, NavIcon } from './NavIcon';
import { ThemeToggle } from './ThemeToggle';

interface Profile {
  displayName: string | null;
  email: string | null;
  isAdmin: boolean;
  /** Open requests — only an administrator is told about them. */
  pendingApprovalCount?: number;
}

const NAVIGATION: Array<{
  to: string;
  label: string;
  icon: IconName;
  end?: boolean;
  adminOnly: boolean;
}> = [
  { to: '/', label: 'Overview', icon: 'overview', end: true, adminOnly: true },
  { to: '/modules', label: 'API modules', icon: 'modules', adminOnly: true },
  { to: '/applications', label: 'Applications', icon: 'applications', adminOnly: false },
  { to: '/roles', label: 'Roles', icon: 'roles', adminOnly: true },
  { to: '/users', label: 'Users', icon: 'users', adminOnly: true },
  { to: '/approvals', label: 'Approvals', icon: 'approvals', adminOnly: true },
  { to: '/audit', label: 'Audit log', icon: 'audit', adminOnly: true },
  { to: '/consents', label: 'My consents', icon: 'consents', adminOnly: false },
];

export function Layout({ profile, children }: { profile: Profile; children: ReactNode }) {
  const items = NAVIGATION.filter((item) => profile.isAdmin || !item.adminOnly);
  const [theme, setTheme] = useThemePreference();

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="brand">
          <BrandMark />
          <h1 className="brand-name">
            Solutec <span className="thin">Data Gateway</span>
          </h1>
        </div>
        <p className="subtitle">Access control</p>

        <nav>
          {items.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end}>
              <NavIcon name={item.icon} />
              <span>{item.label}</span>
              {item.to === '/approvals' && profile.pendingApprovalCount ? (
                <span className="nav-badge">{profile.pendingApprovalCount}</span>
              ) : null}
            </NavLink>
          ))}
        </nav>

        <div className="spacer" />

        <div className="account">
          <strong title={profile.email ?? undefined}>
            {profile.displayName ?? profile.email ?? 'Signed in'}
          </strong>
          <span>{profile.isAdmin ? 'Administrator' : 'User'}</span>
          <div className="account-actions">
            <div className="account-links">
              <NavLink className="link" to="/settings">
                Settings
              </NavLink>
              <button className="link" onClick={() => void signOut()}>
                Sign out
              </button>
            </div>
            <ThemeToggle preference={theme} onChange={setTheme} />
          </div>
        </div>
      </aside>

      <main className="content">{children}</main>
    </div>
  );
}
